import { ForwardedRef, forwardRef, useCallback, useMemo } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  useFormContext,
  UseFormSetValue,
} from 'react-hook-form';

import { useMergeRefs } from '@xemplo/ref-utils';

import { SearchField } from './search-field';
import { SearchFieldRhfProps } from './search-field.types';

type FieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: ControllerRenderProps<TFieldValues, TName>;
  setValue: UseFormSetValue<FieldValues>;
};

export const SearchFieldRhf = forwardRef(
  (props: SearchFieldRhfProps, ref?: ForwardedRef<HTMLInputElement>) => {
    const { name, rules } = props;
    const { control, setValue } = useFormContext();
    const renderField = useMemo(() => fieldFactory(props, ref), [props, ref]);

    return (
      <Controller
        name={name}
        control={control}
        rules={rules}
        shouldUnregister={rules?.shouldUnregister}
        render={({ field }) => renderField({ field, setValue })}
      />
    );
  },
);

/**
 * In order to be able to use the `useMergeRefs`, had to extract the component render.
 * This should ensure correctness of the of the react-hook-form integration.
 **/
const fieldFactory = (
  props: SearchFieldRhfProps,
  ref?: ForwardedRef<HTMLInputElement>,
) => {
  return ({ field, setValue }: FieldProps) => {
    const mergedRefs = useMergeRefs([field.ref, ref]);

    const handleClear = useCallback(() => {
      setValue(field.name, '');
    }, [field.name, setValue]);

    return (
      <SearchField
        {...props}
        required={!!props.rules?.required}
        ref={mergedRefs}
        onChange={(e) => {
          field.onChange(e);
          props.onChange?.(e);
        }}
        onBlur={(e) => {
          field.onBlur();
          props.onBlur?.(e);
        }}
        onClear={handleClear}
        value={field.value ?? ''}
      />
    );
  };
};
